<template>
  <form class="contact--form" @submit.prevent="handleSubmit">
    <div v-if="hasCustomAttributes" class="steps-indicator">
      <div :class="['step', { active: currentStep === 1 }]">1</div>

      <div class="line"></div>

      <div :class="['step', { active: currentStep === 2 }]">2</div>
    </div>

    <template v-if="currentStep === 1">
      <div class="row">
        <div class="columns">
          <woot-avatar-uploader
            :label="$t('CONTACT_FORM.FORM.AVATAR.LABEL')"
            :src="avatarUrl"
            :username-avatar="name"
            :delete-avatar="!!avatarUrl"
            class="settings-item"
            @change="handleImageUpload"
            @onAvatarDelete="handleAvatarDelete"
          />
        </div>
      </div>

      <div class="row">
        <div class="columns">
          <label :class="{ error: $v.name.$error }">
            {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}

            <input
              v-model.trim="name"
              type="text"
              :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
              @input="$v.name.$touch"
            />
          </label>
        </div>
      </div>

      <div class="row">
        <div class="medium-12 columns">
          <label :class="{ error: isPhoneNumberNotValid }">
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}

            <woot-phone-input
              v-model="phoneNumber"
              :encrypt="readonlyPhoneNumber"
              :value="phoneNumber"
              :error="isPhoneNumberNotValid"
              :preferred-countries="['br']"
              :default-country="'br'"
              :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
              :mask="true"
              :readonly="readonlyPhoneNumber"
              @input="onPhoneNumberInputChange"
              @blur="$v.phoneNumber.$touch"
              @setCode="setPhoneCode"
            />

            <span v-if="isPhoneNumberNotValid" class="message">
              {{ phoneNumberError }}
            </span>
          </label>

          <div
            v-if="isPhoneNumberNotValid || !phoneNumber"
            class="callout small warning"
          >
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.HELP') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="columns">
          <label :class="{ error: $v.selectedInbox.$error }">
            {{ $t('CONTACT_FORM.FORM.PREFERRED_INBOX.LABEL') }}

            <select v-model="selectedInbox">
              <option value="">
                {{ $t('CONTACT_FORM.FORM.PREFERRED_INBOX.NONE') }}
              </option>

              <option v-for="item in inboxes" :key="item.id" :value="item.id">
                {{ item.name }}
              </option>
            </select>

            <span v-if="$v.selectedInbox.$error" class="message">
              {{ $t('CONTACT_FORM.FORM.PREFERRED_INBOX.ERROR') }}
            </span>
          </label>

          <div class="callout small warning">
            {{ $t('CONTACT_FORM.FORM.PREFERRED_INBOX.HELP') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="columns">
          <label :class="{ error: $v.email.$error }">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}

            <input
              v-model.trim="email"
              type="text"
              :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
              @input="$v.email.$touch"
            />

            <span v-if="$v.email.$error" class="message">
              {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
            </span>
          </label>
        </div>
      </div>

      <div class="medium-12 columns">
        <label :class="{ error: $v.description.$error }">
          {{ $t('CONTACT_FORM.FORM.BIO.LABEL') }}

          <textarea
            v-model.trim="description"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.BIO.PLACEHOLDER')"
            @input="$v.description.$touch"
          />
        </label>
      </div>

      <div class="medium-12 columns">
        <div class="required-attribute-wrapper">
          <div class="required-header">
            <h4 class="required-title">
              {{ $t('CONTACT_FORM.FORM.BLOCKED_SETTINGS.LABEL') }}
            </h4>

            <p class="required-description">
              {{ $t('CONTACT_FORM.FORM.BLOCKED_SETTINGS.DESCRIPTION') }}
            </p>

            <template v-if="contact.blocked_contact">
              <div class="blocked-contact-warning">
                <span class="blocked-icon">🔒</span>

                <div class="blocked-message">
                  {{
                    $t(
                      'CONTACT_FORM.FORM.BLOCKED_SETTINGS.CONTACT_BLOCKED_MESSAGE'
                    )
                  }}
                </div>
              </div>
            </template>

            <template v-else>
              <div class="switch-row">
                <div class="switch-text">
                  <strong>
                    {{ $t('CONTACT_FORM.FORM.BLOCKED_FROM_MESSAGES.LABEL') }}
                  </strong>

                  <p class="required-description">
                    {{
                      $t('CONTACT_FORM.FORM.BLOCKED_FROM_MESSAGES.DESCRIPTION')
                    }}
                  </p>
                </div>

                <label class="switch">
                  <input v-model="blockedFromMessages" type="checkbox" />

                  <span class="slider"></span>
                </label>
              </div>

              <div class="switch-row">
                <div class="switch-text">
                  <strong>
                    {{ $t('CONTACT_FORM.FORM.BLOCKED_FROM_TEMPLATES.LABEL') }}
                  </strong>

                  <p class="required-description">
                    {{
                      $t('CONTACT_FORM.FORM.BLOCKED_FROM_TEMPLATES.DESCRIPTION')
                    }}
                  </p>
                </div>

                <label class="switch">
                  <input v-model="blockedFromTemplates" type="checkbox" />

                  <span class="slider"></span>
                </label>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template v-if="currentStep === 2">
      <div class="custom-attributes-wrapper">
        <h3 class="custom-attributes-title">Atributos personalizados</h3>

        <div
          v-for="attribute in contactCustomAttributes"
          :key="attribute.id"
          class="custom-attribute-item"
        >
          <label>
            {{ attribute.attribute_display_name }}

            <span v-if="attribute.is_required" class="required-indicator">
              *
            </span>

            <input
              v-if="
                ['text', 'link', 'currency', 'percent'].includes(
                  attribute.attribute_display_type
                )
              "
              v-model="customAttributes[attribute.attribute_key]"
              type="text"
            />

            <input
              v-else-if="attribute.attribute_display_type === 'number'"
              v-model="customAttributes[attribute.attribute_key]"
              type="number"
            />

            <input
              v-else-if="attribute.attribute_display_type === 'date'"
              v-model="customAttributes[attribute.attribute_key]"
              type="date"
            />

            <label
              v-else-if="attribute.attribute_display_type === 'checkbox'"
              class="checkbox-wrapper"
            >
              <input
                v-model="customAttributes[attribute.attribute_key]"
                type="checkbox"
              />

              <span>Ativado</span>
            </label>

            <select
              v-else-if="attribute.attribute_display_type === 'list'"
              v-model="customAttributes[attribute.attribute_key]"
            >
              <option disabled value="">Selecione uma opção</option>

              <option
                v-for="value in attribute.attribute_values"
                :key="value"
                :value="value"
              >
                {{ value }}
              </option>
            </select>

            <input
              v-else
              v-model="customAttributes[attribute.attribute_key]"
              type="text"
            />

            <span
              v-if="
                showCustomAttributesErrors &&
                attribute.is_required &&
                isCustomAttributeEmpty(attribute)
              "
              class="required-message"
            >
              Campo obrigatório
            </span>
          </label>
        </div>
      </div>
    </template>

    <div class="modal-footer">
      <div class="medium-12 columns footer-buttons">
        <woot-button
          v-if="hasCustomAttributes && currentStep > 1"
          class="button clear"
          @click.prevent="prevStep"
        >
          Voltar
        </woot-button>

        <woot-button
          v-if="hasCustomAttributes && currentStep < 2"
          @click.prevent="nextStep"
        >
          Próximo
        </woot-button>

        <woot-submit-button
          v-if="!hasCustomAttributes || currentStep === 2"
          :loading="inProgress"
          :button-text="$t('CONTACT_FORM.FORM.SUBMIT')"
        />

        <button
          v-if="currentStep === 1"
          class="button clear"
          @click.prevent="onCancel"
        >
          {{ $t('CONTACT_FORM.FORM.CANCEL') }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import agentMixin from 'dashboard/mixins/agentMixin';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { required, email } from 'vuelidate/lib/validators';
import countries from 'shared/constants/countries.js';
import {
  isPhoneNumberValid,
  isPtBRPhoneNoZeroStart,
} from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';

export default {
  mixins: [alertMixin, agentMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    inProgress: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      countries,
      companyName: '',
      description: '',
      email: '',
      name: '',
      phoneNumber: '',
      blockedFromMessages: false,
      blockedFromTemplates: false,
      activeDialCode: '+55',
      country: {
        id: 'BR',
        name: 'Brasil',
      },
      avatarFile: null,
      avatarUrl: '',
      city: '',
      socialProfileUserNames: {
        facebook: '',
        twitter: '',
        linkedin: '',
        github: '',
      },
      socialProfileKeys: [
        { key: 'facebook', prefixURL: 'https://facebook.com/' },
        { key: 'twitter', prefixURL: 'https://twitter.com/' },
        { key: 'linkedin', prefixURL: 'https://linkedin.com/' },
        { key: 'github', prefixURL: 'https://github.com/' },
      ],
      selectedInbox: '',
      currentStep: 1,
      customAttributes: {},
      showCustomAttributesErrors: false,
    };
  },
  validations: {
    name: {
      required,
    },
    description: {},
    email: {
      email,
    },
    companyName: {},
    phoneNumber: {
      isPtBRPhoneNoZeroStart,
    },
    bio: {},
    selectedInbox: {},
  },
  computed: {
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    contactCustomAttributes() {
      return (
        this.$store.getters['attributes/getAttributesByModel'](
          'contact_attribute'
        ) || []
      );
    },
    requiredContactCustomAttributes() {
      return this.contactCustomAttributes.filter(
        attribute => attribute.is_required === true
      );
    },
    hasCustomAttributes() {
      return this.contactCustomAttributes.length > 0;
    },
    isPhoneNumberNotValid() {
      if (this.phoneNumber !== '') {
        return (
          !isPhoneNumberValid(this.phoneNumber, this.activeDialCode) ||
          (this.phoneNumber !== '' ? this.activeDialCode === '' : false)
        );
      }

      return false;
    },
    phoneNumberError() {
      if (this.activeDialCode === '') {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }

      if (!isPhoneNumberValid(this.phoneNumber, this.activeDialCode)) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }

      return '';
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }

      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }

      return this.activeDialCode
        ? `${this.activeDialCode}${this.phoneNumber}`
        : '';
    },
    readonlyPhoneNumber() {
      return (
        this.agentRules?.mask_number === false &&
        this.contact.hasOwnProperty('phone_number') &&
        this.contact.phone_number !== null &&
        this.contact.phone_number !== ''
      );
    },
  },
  watch: {
    contact(newContact, oldContact) {
      const newContactId = newContact && newContact.id;
      const oldContactId = oldContact && oldContact.id;

      if (newContactId !== oldContactId) {
        this.setContactObject();
      }
    },
  },
  async mounted() {
    this.setContactObject();
    this.setDialCode();

    await this.$store.dispatch('attributes/get', 1);
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    nextStep() {
      this.$v.$touch();

      if (this.$v.$invalid || this.isPhoneNumberNotValid) {
        return;
      }

      this.currentStep = 2;
    },
    prevStep() {
      this.currentStep = 1;
    },
    setDialCode() {
      if (
        this.phoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      }
    },
    setContactObject() {
      const {
        email: emailAddress,
        phone_number: phoneNumber,
        name,
        blocked_from_messages: blockedFromMessages,
        blocked_from_templates: blockedFromTemplates,
        custom_attributes: savedCustomAttributes = {},
      } = this.contact;

      const additionalAttributes = this.contact.additional_attributes || {};

      this.name = name || '';
      this.email = emailAddress || '';
      this.phoneNumber = phoneNumber || '';
      this.blockedFromMessages = blockedFromMessages || false;
      this.blockedFromTemplates = blockedFromTemplates || false;
      this.companyName = additionalAttributes.company_name || '';

      this.country = {
        id: additionalAttributes.country_code || '',
        name:
          additionalAttributes.country ||
          this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY'),
      };

      this.city = additionalAttributes.city || '';
      this.description = additionalAttributes.description || '';
      this.avatarUrl = this.contact.thumbnail || '';
      this.selectedInbox = additionalAttributes.inbox_id || '';

      this.customAttributes = {};
      this.contactCustomAttributes.forEach(attr => {
        if (savedCustomAttributes.hasOwnProperty(attr.attribute_key)) {
          this.$set(
            this.customAttributes,
            attr.attribute_key,
            savedCustomAttributes[attr.attribute_key]
          );
        } else {
          this.$set(
            this.customAttributes,
            attr.attribute_key,
            attr.attribute_display_type === 'checkbox' ? false : ''
          );
        }
      });
    },
    getContactObject() {
      const contactObject = {
        id: this.contact.id,
        name: this.name,
        email: this.email,
        phone_number: this.setPhoneNumber,
        blocked_from_messages: this.blockedFromMessages,
        blocked_from_templates: this.blockedFromTemplates,

        additional_attributes: {
          ...this.contact.additional_attributes,
          description: this.description,
          company_name: this.companyName,
          country_code: this.country.id,

          country:
            this.country.name ===
            this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')
              ? ''
              : this.country.name,

          city: this.city,
          social_profiles: this.socialProfileUserNames,
          inbox_id: this.selectedInbox,
        },

        custom_attributes: this.customAttributes,
      };

      if (this.avatarFile) {
        contactObject.avatar = this.avatarFile;
        contactObject.isFormData = true;
      }

      return contactObject;
    },
    onPhoneNumberInputChange(value, code) {
      this.activeDialCode = code;
      this.phoneNumber = value;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;

        if (dialCode === code) {
          return;
        }

        this.activeDialCode = `+${dialCode}`;

        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );

        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    isCustomAttributeEmpty(attribute) {
      const value = this.customAttributes[attribute.attribute_key];

      if (attribute.attribute_display_type === 'checkbox') {
        return value !== true;
      }

      return value === undefined || value === null || value === '';
    },
    validateRequiredCustomAttributes() {
      return this.requiredContactCustomAttributes.every(attribute => {
        return !this.isCustomAttributeEmpty(attribute);
      });
    },
    async handleSubmit() {
      this.$v.$touch();

      if (this.$v.$invalid || this.isPhoneNumberNotValid) {
        return;
      }

      this.showCustomAttributesErrors = true;

      if (
        this.hasCustomAttributes &&
        !this.validateRequiredCustomAttributes()
      ) {
        this.showAlert('Preencha todos os atributos obrigatórios.');
        return;
      }

      try {
        await this.onSubmit(this.getContactObject());

        this.onSuccess();

        this.showAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          if (error.data.includes('email')) {
            this.showAlert(
              this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE')
            );
          } else if (error.data.includes('phone_number')) {
            this.showAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);

          this.showAlert(
            this.$t('CONTACT_FORM.DELETE_AVATAR.API.SUCCESS_MESSAGE')
          );
        }

        this.avatarFile = null;
        this.avatarUrl = '';
        this.activeDialCode = '';
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('CONTACT_FORM.DELETE_AVATAR.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.contact--form {
  padding: var(--space-normal) var(--space-large) var(--space-large);

  .columns {
    padding: 0 var(--space-smaller);
  }
}

.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.step {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e5e5e5;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.2s ease;
}

.step.active {
  background: #2563eb;
  color: #ffffff;
}

.line {
  width: 130px;
  height: 4px;
  background: #d1d5db;
  margin: 0 18px;
  border-radius: 999px;
}

.custom-attributes-wrapper {
  margin-top: 8px;
}

.custom-attributes-title {
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 700;
}

.custom-attribute-item {
  margin-bottom: 20px;
}

.custom-attribute-item input,
.custom-attribute-item select {
  margin-top: 8px;
}

.required-indicator {
  color: #ef4444;
  margin-left: 4px;
  font-weight: 700;
}

.required-message {
  display: block;
  margin-top: 6px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.footer-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.input-group-label {
  font-size: var(--font-size-small);
}

::v-deep {
  .multiselect .multiselect__tags .multiselect__single {
    padding-left: 0;
  }
}

.required-attribute-wrapper {
  margin-top: 12px;
  margin-bottom: var(--space-large);
  padding: 16px;
  border: 1px solid var(--s-100);
  border-radius: 12px;
  background: var(--white);
}

.required-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.required-title {
  margin: 0 0 6px;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semibold);
  color: var(--s-900);
}

.required-description {
  margin: 0 0 16px;
  font-size: var(--font-size-small);
  line-height: 1.5;
  color: var(--s-600);
}

.switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 56px;
  height: 32px;
  cursor: pointer;
}

.switch input {
  display: none;
}

.slider {
  position: relative;
  width: 56px;
  height: 32px;
  background-color: #cbd5e1;
  border-radius: 999px;
  transition: background-color 0.3s ease;
}

.slider::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  left: 4px;
  top: 4px;
  border-radius: 50%;
  background-color: #ffffff;
  transition: transform 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.switch input:checked + .slider {
  background-color: rgb(19, 88, 153);
}

.switch input:checked + .slider::before {
  transform: translateX(24px);
}

.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
}

.switch-row:first-of-type {
  margin-top: 0;
  padding-top: 0;
}

.switch-text p {
  margin: 2px 0 0;
  line-height: 1.3;
}

.switch.disabled {
  cursor: not-allowed;
  .slider {
    background: #d1d5db !important;
    opacity: 0.55;
  }
  .slider::before {
    background: #f8fafc;
    transform: none !important;
  }
}

.switch.disabled input {
  pointer-events: none;
}

.switch-text.disabled {
  opacity: 0.55;
  strong,
  p {
    color: var(--s-500);
  }
}

.blocked-contact-warning {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  .blocked-icon {
    font-size: 18px;
    line-height: 1.4;
  }
  .blocked-message {
    line-height: 1.5;
    font-size: var(--font-size-small);
  }
}
</style>
